import React, { useEffect, useRef, useState } from 'react';

import { IconKey } from '../../../types/system/icons';

import InputIconByKey from '../../Forms/Input/InputIconByKey';

interface OptionItem {
  value: string | number;

  label: string;
}

interface POSSelectFieldProps {
  label: string;

  name: string;

  value: string | number;

  options: OptionItem[];

  onChange: (event: {
    target: {
      name: string;
      value: string | number;
      options: { text: string }[];
      selectedIndex: number;
    };
  }) => void;

  placeholder?: string;

  iconKey?: IconKey;

  disabled?: boolean;

  className?: string;

  rightElement?: React.ReactNode;

  error?: string;
}

const POSSelectField: React.FC<POSSelectFieldProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Seleccionar',
  iconKey,
  disabled,
  className = '',
  rightElement,
  error,
}) => {
  // =====================================================
  // 🔹 State
  // =====================================================
  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // =====================================================
  // 🔹 Selected
  // =====================================================
  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );

  // =====================================================
  // 🔹 Outside Click
  // =====================================================
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div ref={containerRef} className={`flex flex-col gap-1.5 ${className}`}>
      {/* Label */}
      <label
        className="
        text-xs
        font-medium
        text-body
        dark:text-bodydark
      "
      >
        {label}
      </label>

      <div className="flex items-start gap-2">
        {/* Select */}
        <div className="relative w-full">
          {/* Campo */}
          <div
            className={`
            flex
            min-h-[44px]
            w-full
            items-stretch

            overflow-hidden
            rounded-xl

            border

            ${
              error
                ? 'border-danger'
                : isOpen
                ? 'border-primary'
                : 'border-stroke dark:border-strokedark'
            }

            bg-white
            shadow-1

            transition-all

            duration-200
            dark:bg-boxdark-2

            ${!disabled ? 'hover:border-primary hover:shadow-2' : ''}

            ${disabled ? 'cursor-not-allowed opacity-60' : ''}
          `}
          >
            {/* Icon */}
            {iconKey && (
              <div
                className="
                flex
                w-12
                shrink-0
                items-center
                justify-center

                border-r
                border-stroke

                bg-gray

                dark:border-strokedark
                dark:bg-meta-4
              "
              >
                <InputIconByKey iconKey={iconKey} />
              </div>
            )}

            {/* Trigger */}
            <button
              type="button"
              disabled={disabled}
              onClick={() => setIsOpen((prev) => !prev)}
              className="
              flex
              w-full
              items-center
              justify-between
              gap-3

              px-4
              py-3

              text-left

              transition-colors
            "
            >
              <span
                className={`
                flex-1
                truncate

                text-sm
                font-medium

                ${
                  selectedOption
                    ? 'text-black dark:text-white'
                    : 'text-body dark:text-bodydark2'
                }
              `}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>

              <svg
                className={`
                h-4
                w-4
                shrink-0

                transition-all
                duration-200

                ${isOpen ? 'rotate-180 text-primary' : 'text-bodydark2'}
              `}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {/* Dropdown */}
          {isOpen && (
            <div
              className="
              absolute
              left-0
              top-full

              z-9999
              mt-2

              max-h-64
              w-full

              overflow-y-auto

              rounded-xl
              border
              border-stroke

              bg-white
              shadow-4
              dark:border-strokedark

              dark:bg-boxdark
            "
            >
              {options.map((option) => {
                const selected = String(option.value) === String(value);

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange({
                        target: {
                          name,
                          value: option.value,
                          options: options.map((opt) => ({
                            text: opt.label,
                          })),
                          selectedIndex: options.findIndex(
                            (opt) => String(opt.value) === String(option.value)
                          ),
                        },
                      });

                      setIsOpen(false);
                    }}
                    className={`
                    flex
                    w-full
                    items-center

                    px-4
                    py-3

                    text-left
                    text-sm

                    transition-all

                    ${
                      selected
                        ? `
                          bg-primary/10
                          font-semibold
                          text-primary
                        `
                        : `
                          text-black
                          hover:bg-gray

                          dark:text-bodydark1
                          dark:hover:bg-meta-4
                        `
                    }
                  `}
                  >
                    <span className="truncate">{option.label}</span>

                    {selected && (
                      <svg
                        className="
                        ml-auto
                        h-4
                        w-4

                        text-primary
                      "
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Extra Button */}
        {rightElement}
      </div>

      {/* Error */}
      {error && (
        <p
          className="
          mt-1
          pl-1

          text-xs
          font-medium

          text-danger
        "
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default POSSelectField;
